import { ROLES, type Role } from '@shared/domain';

/**
 * In-memory access-token store. The token is deliberately never persisted (no localStorage):
 * a page reload restores the session through the httpOnly refresh cookie instead.
 */
export interface Claims {
  userId: string;
  tenantId: string;
  organizationId?: string;
  role: Role;
  expiresAt: number;
}

type Listener = () => void;

let accessToken: string | null = null;
let claims: Claims | null = null;
const listeners = new Set<Listener>();

function decode(token: string): Claims | null {
  const segment = token.split('.')[1];
  if (!segment) return null;
  try {
    const json: unknown = JSON.parse(atob(segment.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof json !== 'object' || json === null) return null;
    const p = json as Record<string, unknown>;
    if (typeof p.sub !== 'string' || typeof p.tid !== 'string' || typeof p.exp !== 'number') return null;
    if (!ROLES.includes(p.role as Role)) return null;
    return {
      userId: p.sub,
      tenantId: p.tid,
      role: p.role as Role,
      expiresAt: p.exp * 1000,
      ...(typeof p.oid === 'string' ? { organizationId: p.oid } : {}),
    };
  } catch {
    return null;
  }
}

export const session = {
  getToken: (): string | null => accessToken,
  getClaims: (): Claims | null => claims,
  set(token: string): void {
    const decoded = decode(token);
    if (!decoded) throw new Error('Received a malformed access token');
    accessToken = token;
    claims = decoded;
    listeners.forEach((l) => l());
  },
  clear(): void {
    if (accessToken === null) return;
    accessToken = null;
    claims = null;
    listeners.forEach((l) => l());
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
